import {
  Component,
  OnInit,
  LOCALE_ID,
  inject,
  ChangeDetectionStrategy,
  DestroyRef,
  input,
  model,
  output,
} from '@angular/core';
import { ToastService } from '@app/core/services/toast/toast.service';
import {
  CartonGroupingSearchAttributes,
  CartonGroupingsGQL,
  CartonGroupingsQueryVariables,
} from '@app/generated/graphql';
import { DecimalPipe, TitleCasePipe } from '@angular/common';
import { CartonGroupingViewModel } from '@app/core/view-models/carton-grouping.viewmodel';
import { ExcelHelper } from '@app/core/helpers/excel.helper';
import {
  AggregatedColumn,
  ColumnMapping,
} from '@app/core/components-openagri/agx-pivot-table/agx-pivot-table.component';
import { Aggregators } from '@app/core/components-openagri/agx-pivot-table/aggregators';
import { AgxPivotTableComponent } from '../../components-openagri/agx-pivot-table/agx-pivot-table.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CartonGroupingPivotTableMixpanelEventTrackingService } from './carton-grouping-pivot-table-mixpanel-event-tracking.service';
import { catchError, EMPTY, tap } from 'rxjs';
import { ErrorService } from '@app/core/services/error/error.service';

@Component({
  selector: 'ag-carton-grouping-pivot-table',
  templateUrl: './carton-grouping-pivot-table.component.html',
  styleUrls: ['./carton-grouping-pivot-table.component.css'],
  providers: [TitleCasePipe],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [AgxPivotTableComponent],
})
export class CartonGroupingPivotTableComponent implements OnInit {
  private _destroyRef = inject(DestroyRef);
  private _errorService = inject(ErrorService);
  locale = inject(LOCALE_ID);
  private cartonGroupingsGql = inject(CartonGroupingsGQL);
  private toastService = inject(ToastService);
  private _cartonGroupingPivotTableMixpanelTracking = inject(
    CartonGroupingPivotTableMixpanelEventTrackingService,
  );
  private titleCase = inject(TitleCasePipe);

  readonly loading = output<boolean>();

  readonly canDownload = output<boolean>();

  readonly companyId = input<number>(undefined);

  readonly companyIds = input<number[]>(undefined);

  readonly orderId = input<number>(undefined);

  readonly packingWeekFrom = input<string>(undefined);

  readonly packingWeekTo = input<string>(undefined);

  readonly limit = input<number>(undefined);

  readonly loadAtStart = input(false);

  readonly selectedColumns = model<ColumnMapping[]>([]);
  readonly scrollHeight = input<string>(undefined);

  readonly aggregatedTotalType = input('cartons');

  private _cartonGroupings: CartonGroupingViewModel[] = [];
  private _isLoading = this.loadAtStart();

  set isLoading(value: boolean) {
    this._isLoading = value;
    this.loading.emit(value);
  }

  set cartonGroupings(value: CartonGroupingViewModel[]) {
    this._cartonGroupings = value;
    this.canDownload.emit(this._cartonGroupings.length > 0);
  }

  get isLoading(): boolean {
    return this._isLoading;
  }

  get cartonGroupings(): CartonGroupingViewModel[] {
    return this._cartonGroupings;
  }

  get filterArgs(): CartonGroupingsQueryVariables {
    const args: CartonGroupingSearchAttributes = {};
    const companyIdsSet = new Set<number>();

    const companyIdValue: number = this.companyId();
    if (companyIdValue) {
      companyIdsSet.add(companyIdValue);
    }

    const companyIds: number[] = this.companyIds();
    if (companyIds) {
      companyIds.forEach((companyId) => companyIdsSet.add(companyId));
    }

    if (companyIdsSet.size > 0) {
      args.companyIds = Array.from(companyIdsSet);
    }

    const orderId: number = this.orderId();
    if (orderId) {
      args.orderId = orderId;
    }
    const packingWeekFrom: string = this.packingWeekFrom();
    if (packingWeekFrom) {
      args.packingWeekFrom = packingWeekFrom;
    }
    const packingWeekTo: string = this.packingWeekTo();
    if (packingWeekTo) {
      args.packingWeekTo = packingWeekTo;
    }
    const limit: number = this.limit();
    if (limit) {
      args.limit = limit;
    } else {
      args.limit = 1000;
    }
    return { attributes: args };
  }

  columnMappings: ColumnMapping[] = [
    { heading: 'Batch Number', property: 'batchNumber' },
    { heading: 'Buyer', property: 'buyerName' },
    { heading: 'Cold Store', property: 'coldStore' },
    { heading: 'Commodity', property: 'commodity' },
    { heading: 'Consignment Number', property: 'consignmentNumber' },
    { heading: 'Container', property: 'containerNumber' },
    { heading: 'Exporter Code', property: 'exporterCode' },
    { heading: 'Farm Code', property: 'farmCode' },
    { heading: 'Grade', property: 'grade' },
    { heading: 'Incoterm', property: 'incoterm' },
    { heading: 'Inventory Code', property: 'inventoryCode' },
    { heading: 'Line Item #', property: 'lineItemId' },
    { heading: 'Mark', property: 'mark' },
    { heading: 'Orchard', property: 'orchard' },
    { heading: 'Order #', property: 'orderId' },
    { heading: 'Pack', property: 'pack' },
    { heading: 'Packhouse Code', property: 'packhouseCode' },
    { heading: 'Packing Week', property: 'packingWeek' },
    { heading: 'Pallet Number', property: 'palletNumber' },
    { heading: 'Pallet Stack', property: 'palletStack' },
    { heading: 'Product Characteristic', property: 'productCharacteristic' },
    { heading: 'Reference Number', property: 'referenceNumber' },
    { heading: 'Seller', property: 'sellerName' },
    { heading: 'Sequence Number', property: 'sequenceNumber' },
    { heading: 'Size Count', property: 'sizeCount' },
    { heading: 'State', property: 'state' },
    { heading: 'Target Country', property: 'targetCountry' },
    { heading: 'Target Market', property: 'targetMarket' },
    { heading: 'Target Region', property: 'targetRegion' },
    { heading: 'Variety', property: 'variety' },
  ];

  aggregatedColumns: AggregatedColumn[] = [
    {
      property: 'cartons',
      aggregator: (iterable: number[]) =>
        new DecimalPipe(this.locale).transform(Aggregators.sum(iterable)),
      heading: 'Total Cartons',
    },
  ];

  constructor() {}

  ngOnInit(): void {
    setTimeout(() => {
      if (this.loadAtStart()) {
        this.loadCartonGroupings();
      } else {
        this.isLoading = false;
      }
    });
  }

  loadCartonGroupings() {
    this.isLoading = true;
    this.switchAggregatedTotal();
    this.cartonGroupingsGql
      .fetch({ variables: this.filterArgs })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(
        (res) => {
          this.cartonGroupings =
            res.data.cartonGroupingsSearch.cartonGroupings.map(
              (cg) => new CartonGroupingViewModel(cg),
            );
        },
        (err) => {
          this.toastService.showErrorToast('Unable to load pallets');
          throw err;
        },
      )
      .add(() => {
        this.isLoading = false;
      });
  }

  downloadData(): void {
    ExcelHelper.downloadPivotTableData$(
      'Pallets',
      this.cartonGroupings,
      this.selectedColumns(),
      this.aggregatedColumns,
    )
      .pipe(
        tap(() => {
          this._cartonGroupingPivotTableMixpanelTracking.trackExportPallets();
        }),
        catchError((error) => {
          this._errorService.handleError(error, 'Failed to export Excel file');
          return EMPTY;
        }),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe();
  }

  switchAggregatedTotal() {
    const propertyHeading = this.titleCase.transform(
      'Total ' + this.aggregatedTotalType(),
    );

    this.aggregatedColumns = [
      {
        property: this.aggregatedTotalType(),
        aggregator: (iterable: number[]) =>
          new DecimalPipe(this.locale).transform(Aggregators.sum(iterable)),
        heading: propertyHeading,
      },
    ];
  }
}
